/**
 * O cliente da API da OS do sistema (`/api/v1/os`, fase 3). Os tipos espelham as respostas de `server/lib/os-sistema/*`.
 * Erro de regra chega com a FRASE em `mensagem`: `mensagemDoErro` a devolve para a tela.
 */
import { api, ApiError } from '@/lib/api';

export type StatusDaOs = 'agendada' | 'a_caminho' | 'em_execucao' | 'pausada' | 'finalizada' | 'finalizada_pendencia' | 'cancelada';

export interface ItemDaAgenda {
  id: string;
  origem: 'sistema' | 'auvo';
  numero: string;
  data: string;
  hora: string | null;
  duracaoMin: number;
  status: StatusDaOs | 'auvo_aberta' | 'auvo_finalizada';
  clienteId: string | null;
  clienteNome: string;
  natureza: string;
  grupo: string;
  sistemaNome: string | null;
  tecnicoId: string | null;
  tecnicoNome: string;
  empresa: 'IGM' | 'ROUTE';
  chamados: number[];
  checkinLonge: boolean;
}

export interface OsSemData {
  id: string;
  numero: number;
  clienteId: string;
  clienteNome: string;
  natureza: string;
  sistemaNome: string | null;
  solicitacao: string | null;
  chamados: number[];
  criadaEmIso: string;
}

export interface PendenciaAberta {
  osId: string;
  numero: number;
  clienteId: string;
  clienteNome: string;
  motivo: string | null;
  concluidaEm: string | null;
  retorno: { numero: number; status: StatusDaOs; data: string | null } | null;
}

export interface DetalheDaOs {
  id: string;
  numero: number;
  status: StatusDaOs;
  clienteId: string;
  clienteNome: string;
  endereco: string | null;
  natureza: string;
  naturezaId: string;
  sistemaId: string | null;
  sistemaNome: string | null;
  empresa: 'IGM' | 'ROUTE';
  tecnicoId: string | null;
  tecnicoNome: string | null;
  agendadaData: string | null;
  agendadaHora: string | null;
  solicitacao: string | null;
  relato: string | null;
  aCaminhoEm: string | null;
  entradaEm: string | null;
  saidaEm: string | null;
  entradaDistanciaM: number | null;
  entradaJustificativa: string | null;
  pendencia: boolean;
  pendenciaMotivo: string | null;
  pendenciaResolvida: boolean;
  retornoDeOs: number | null;
  retornoAberto: { id: string; numero: number; status: StatusDaOs; data: string | null } | null;
  chamados: Array<{ id: string; numero: number; titulo: string; status: string; origem: string }>;
  fotos: Array<{ id: string; origem: 'camera' | 'galeria'; tiradaEmParede: string | null; legenda: string | null; temLocalizacao: boolean; foraDoRelatorio: boolean }>;
  assinatura: { nome: string; temCpf: boolean; assinadaEm: string } | null;
  respostas: Array<{ codigo: string; pergunta: string; valor: unknown; publica: boolean }>;
  material: Array<{ codigo: string; descricao: string; unidade: string | null; quantidade: number; custoUnitarioCentavos: number | null }>;
  eventos: Array<{ tipo: string; emParede: string; autorNome: string | null; detalhe: unknown }>;
  linkToken: string | null;
}

export interface Chamado {
  id: string;
  numero: number;
  clienteId: string;
  clienteNome: string;
  sistemaId: string | null;
  sistemaNome: string | null;
  titulo: string;
  descricao: string | null;
  solicitante: string | null;
  origem: 'portal' | 'interno' | 'auvodesk' | 'whatsapp';
  idExterno: string | null;
  status: 'aberto' | 'encerrado';
  abertoEmParede: string;
  encerradoEmParede: string | null;
  encerrarNoDesk: boolean;
  os: number[];
}

export interface Natureza {
  id: string;
  nome: string;
  grupo: string;
  ativo: boolean;
  ordem: number;
}

export interface Tecnico {
  userId: string;
  nome: string;
  noPiloto: boolean;
}

/** O técnico que entra com e-mail e senha (`server/lib/tecnico-de-campo/acesso.ts`, `TecnicoDeCampo`). */
export interface TecnicoDeCampo {
  userId: string;
  nome: string;
  email: string;
  situacao: 'convidado' | 'ativo' | 'desativado';
  ultimoAcesso: string | null;
}

export interface Agendamento {
  data: string;
  hora: string;
  tecnicoId: string;
}

export interface PreviaDoDia {
  itens: ItemDaAgenda[];
  conflitos: string[];
}

export interface ResultadoDaBusca {
  id: string;
  numero: number;
  data: string | null;
  clienteNome: string;
  servico: string;
  tecnicoNome: string | null;
  status: StatusDaOs;
}

export interface RegraDoServico {
  id: string;
  naturezaId: string;
  sistemaId: string | null;
  exigeRelato: boolean;
  exigeAssinatura: boolean;
  exigeCpfAssinante: boolean;
  fotosMinimas: number;
  questionarioTemplateId: string | null;
  duracaoEstimadaMin: number | null;
  ativo: boolean;
}

export interface ConfigDaOs {
  distanciaCheckinM: number;
  escadaMin: [number, number, number];
  pilotoLigado: boolean;
  atualizadoPor: string | null;
  atualizadoEm: string | null;
}

/** A frase que a tela mostra para um erro da API (a do servidor, quando ele manda). */
export function mensagemDoErro(e: unknown, padrao = 'Não deu certo. Tente de novo.'): string {
  if (e instanceof ApiError) {
    const dados = e.response as { mensagem?: string } | undefined;
    return dados?.mensagem ?? padrao;
  }
  return padrao;
}

/** D72 (mig 789): o planejamento das preventivas — espelha `server/lib/os-sistema/preventivas-plano.ts`. */
export interface ClienteDoPlano {
  id: string;
  nome: string;
  sistemas: string;
  regra: { semana: number; diaSemana: number; tecnicoId: string | null; texto: string } | null;
  preFuturos: number;
  /** "AAAA-MM": os meses que o contrato pede e ainda não têm pré-agendamento (o que põe o cliente na lista). */
  mesesEmAberto: string[];
}
export interface MesDoPlano {
  mes: string;
  ganha: boolean;
  data: string | null;
  movidaDe: string | null;
  noDia: number;
  passou: boolean;
  jaTem: { id: string; data: string; status: string } | null;
}
export interface PreAgendamento {
  id: string;
  clienteId: string;
  clienteNome: string;
  data: string;
  tecnicoId: string | null;
  tecnicoNome: string | null;
  movidaDe: string | null;
}

const q = (o: Record<string, string | null | undefined>): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : '';
};

const B = '/api/v1/os';

export const osApi = {
  agenda: (f: { de: string; ate: string; tecnico?: string | null; cliente?: string | null; natureza?: string | null; sistema?: string | null; status?: string | null }) =>
    api.get<ItemDaAgenda[]>(`${B}/agenda${q(f)}`),
  previa: (f: { tecnico: string; data: string; hora?: string | null; ignorar?: string | null }) => api.get<PreviaDoDia>(`${B}/agenda/previa${q(f)}`),
  semAgendamento: () => api.get<OsSemData[]>(`${B}/sem-agendamento`),
  pendencias: () => api.get<PendenciaAberta[]>(`${B}/pendencias`),
  busca: (f: { q?: string | null; status?: string | null }) => api.get<ResultadoDaBusca[]>(`${B}/busca${q(f)}`),
  detalhe: (id: string) => api.get<DetalheDaOs>(`${B}/ordens/${id}`),
  /** Uma foto (pelo id) ou a assinatura (`'assinatura'`) da OS, para a tela mostrar. */
  arquivo: (osId: string, arquivo: string) => api.blob(`${B}/ordens/${osId}/arquivos/${encodeURIComponent(arquivo)}`),
  chamados: (f: { status?: 'aberto' | 'encerrado' | null; cliente?: string | null; noDesk?: '1' | null } = {}) => api.get<Chamado[]>(`${B}/chamados${q(f)}`),
  tecnicos: () => api.get<Tecnico[]>(`${B}/tecnicos`),
  naturezas: () => api.get<Natureza[]>(`${B}/naturezas`),
  sistemas: () => api.get<Array<{ id: string; nome: string }>>(`${B}/sistemas`),
  questionarios: () => api.get<Array<{ id: string; nome: string }>>(`${B}/questionarios`),
  regras: () => api.get<RegraDoServico[]>(`${B}/regras`),
  config: () => api.get<ConfigDaOs>(`${B}/config`),

  criar: (d: { clienteId: string; naturezaId: string; sistemaId?: string | null; solicitacao?: string | null; chamadoIds?: string[]; agendamento?: Agendamento | null }) =>
    api.post<{ id: string; numero: number }>(`${B}/ordens`, d),
  agendar: (id: string, a: Agendamento) => api.post<DetalheDaOs>(`${B}/ordens/${id}/agendar`, a),
  cancelar: (id: string, motivo: string) => api.post<DetalheDaOs>(`${B}/ordens/${id}/cancelar`, { motivo }),
  vincular: (id: string, chamadoId: string) => api.post<DetalheDaOs>(`${B}/ordens/${id}/chamados`, { chamadoId }),
  desvincular: (id: string, chamadoId: string) => api.delete<DetalheDaOs>(`${B}/ordens/${id}/chamados/${chamadoId}`),
  retorno: (id: string, a: Agendamento) => api.post<{ id: string; numero: number }>(`${B}/ordens/${id}/retorno`, a),
  clientesDoPlano: () => api.get<ClienteDoPlano[]>(`${B}/preventivas/clientes`),
  previaDoPlano: (cliente: string, semana: number, dia: number) => api.get<MesDoPlano[]>(`${B}/preventivas/previa${q({ cliente, semana: String(semana), dia: String(dia) })}`),
  criarPreAgendamentos: (cliente: string, d: { semana: number; diaSemana: number; tecnicoId: string }) => api.post<{ criados: number }>(`${B}/preventivas/clientes/${encodeURIComponent(cliente)}/criar`, d),
  regraDaPreventiva: (clienteId: string) =>
    api.get<{ regra: { texto: string; tecnicoNome: string | null } | null }>(`${B}/preventivas/clientes/${encodeURIComponent(clienteId)}/regra`),
  preAgendamentos: (de: string, ate: string) => api.get<PreAgendamento[]>(`${B}/preventivas/pre${q({ de, ate })}`),
  remanejarPre: (id: string, d: { data: string; tecnicoId: string }) => api.post(`${B}/preventivas/pre/${encodeURIComponent(id)}/remanejar`, d),
  tirarPre: (id: string) => api.post(`${B}/preventivas/pre/${encodeURIComponent(id)}/tirar`, {}),
  confirmarPre: (ids: string[]) =>
    api.post<{ confirmados: Array<{ id: string; osId: string; numero: number }>; recusados: Array<{ id: string; motivo: string }> }>(`${B}/preventivas/pre/confirmar`, { ids }),
  alterarConcluida: (id: string, d: { naturezaId?: string; sistemaId?: string | null; relato?: string }) => api.patch<DetalheDaOs>(`${B}/ordens/${id}/concluida`, d),
  fotoNoRelatorio: (id: string, fotoId: string, mostrar: boolean) => api.post<DetalheDaOs>(`${B}/ordens/${id}/fotos/${encodeURIComponent(fotoId)}/relatorio`, { mostrar }),
  link: (id: string) => api.post<{ token: string; caminho: string }>(`${B}/ordens/${id}/link`, {}),
  criarChamado: (d: { clienteId: string; sistemaId?: string | null; titulo: string; descricao?: string | null; solicitante?: string | null }) =>
    api.post<{ id: string; numero: number }>(`${B}/chamados`, d),
  encerrarChamado: (id: string) => api.post(`${B}/chamados/${id}/encerrar`, {}),
  encerradoNoDesk: (id: string) => api.post(`${B}/chamados/${id}/encerrado-no-desk`, {}),

  criarNatureza: (d: { nome: string; grupo: string }) => api.post<Natureza>(`${B}/naturezas`, d),
  editarNatureza: (id: string, d: { nome?: string; grupo?: string; ativo?: boolean }) => api.patch<Natureza>(`${B}/naturezas/${id}`, d),
  salvarRegra: (d: Omit<RegraDoServico, 'id' | 'ativo'>) => api.put<RegraDoServico>(`${B}/regras`, d),
  salvarConfig: (d: { distanciaCheckinM: number; escadaMin: [number, number, number] }) => api.put<ConfigDaOs>(`${B}/config`, d),
  mudarChave: (chave: 'piloto', ligado: boolean) => api.post<ConfigDaOs>(`${B}/config/chave`, { chave, ligado }),
  marcarPiloto: (userId: string, noPiloto: boolean) => api.post<Tecnico[]>(`${B}/piloto/${userId}`, { noPiloto }),
  // O técnico de campo que entra com e-mail e senha (25/10/2026) — só admin.
  tecnicosDeCampo: () => api.get<TecnicoDeCampo[]>(`${B}/tecnicos-de-campo`),
  convidarTecnico: (d: { nome: string; email: string }) =>
    api.post<{ userId: string; emailSaiu: boolean; tecnicos: TecnicoDeCampo[] }>(`${B}/tecnicos-de-campo`, d),
  reenviarConvite: (userId: string) => api.post<{ emailSaiu: boolean }>(`${B}/tecnicos-de-campo/${userId}/reenviar-convite`),
};
